import * as Yup from 'yup';
import { NumberSchema } from 'yup';

import { CmsProduct, CmsVirtualProduct, ShopContent } from '../../../../types/cms_shared_types';

export const getValidationSchema = (
  cmsProduct: CmsProduct,
  cmsShopContent: ShopContent,
  virtualProduct?: CmsVirtualProduct,
  skippedProductCodes?: Set<string>,
) => {
  if (virtualProduct) {
    const productSchemas = Object.fromEntries(
      virtualProduct.products
        .filter((product) => !skippedProductCodes?.has(product.code))
        .map((product) => [product.code, getProductValidationSchema(product, cmsShopContent)]),
    );

    return Yup.object().shape({
      productValues: Yup.object().shape(productSchemas),
    });
  }

  return getProductValidationSchema(cmsProduct, cmsShopContent);
};

const getProductValidationSchema = (cmsProduct: CmsProduct, cmsShopContent: ShopContent) => {
  return Yup.object().shape(
    {
      measureInput: Yup.string().when('customMeasureInputSelected', {
        is: false,
        then: () => {
          if (cmsProduct.hasMeasure) {
            return cmsProduct.isSchablon
              ? minMaxInputValidation(cmsShopContent, cmsProduct.measureSchablonMin, cmsProduct.measureSchablonMax)
              : minMaxInputValidation(cmsShopContent, cmsProduct.measureMin, cmsProduct.measureMax);
          } else {
            return Yup.string().notRequired();
          }
        },
        otherwise: (schema) => schema.notRequired(),
      }),
      customMeasureInputSelected: Yup.boolean(),
      customMeasureInput: Yup.string().when('customMeasureInputSelected', {
        is: true,
        then: () => minMaxInputValidation(cmsShopContent, cmsProduct.measureOptionsMin, cmsProduct.measureOptionsMax),
        otherwise: (schema) => schema.notRequired(),
      }),
      timeMeasureInput: Yup.string().when('customTimeMeasureInputSelected', {
        is: false,
        then: () => {
          if (cmsProduct.hasTimeMeasure) {
            const maschema = cmsProduct.isSchablon
              ? minMaxInputValidation(
                  cmsShopContent,
                  cmsProduct.timeMeasureSchablonMin,
                  cmsProduct.timeMeasureSchablonMax,
                )
              : minMaxInputValidation(cmsShopContent, cmsProduct.timeMeasureMin, cmsProduct.timeMeasureMax);

            return maschema;
          } else {
            return Yup.string().notRequired();
          }
        },
        otherwise: (schema) => schema.notRequired(),
      }),
      customTimeMeasureInputSelected: Yup.boolean(),
      customTimeMeasureInput: Yup.string().when('customTimeMeasureInputSelected', {
        is: true,
        then: () =>
          minMaxInputValidation(cmsShopContent, cmsProduct.timeMeasureOptionsMin, cmsProduct.timeMeasureOptionsMax),
        otherwise: (schema) => schema.notRequired(),
      }),
      musicClassToggleInput: Yup.boolean().when('musicClassToggleInput', () => {
        if (!cmsProduct.reporting && cmsProduct.musicClass.length === 1) {
          return Yup.boolean().notRequired();
        } else {
          return Yup.boolean().required(cmsShopContent.productChoiceStep.productConfigurationModal.inputErrorMessage);
        }
      }),
      startDate: Yup.date().when('isSeasonallyOpen', {
        is: true,
        then: (schema) => schema.required(cmsShopContent.invalidDateRangeError),
        otherwise: (schema) => schema.notRequired(),
      }),

      endDate: Yup.date().when('isSeasonallyOpen', {
        is: true,
        then: (schema) => schema.required(cmsShopContent.invalidDateRangeError),
        otherwise: (schema) => schema.notRequired(),
      }),
      isSeasonallyOpen: Yup.boolean(),
    },
    // cyclic dependency (see https://stackoverflow.com/a/72402785)
    [
      ['musicClassToggleInput', 'musicClassToggleInput'],
      ['startDate', 'endDate'],
      ['isSeasonallyOpen', 'startDate'],
      ['isSeasonallyOpen', 'endDate'],
    ],
  );
};

const minMaxInputValidation = (
  cmsShopContent: ShopContent,
  min?: number | null,
  max?: number | null,
): NumberSchema<number> => {
  const positiveNumberErrorMessage = cmsShopContent.productChoiceStep.productConfigurationModal.inputErrorMessage;
  const inputTooLowErrorTemplate = cmsShopContent.productChoiceStep.productConfigurationModal.inputTooLowErrorMessage;
  const inputTooHighErrorTemplate = cmsShopContent.productChoiceStep.productConfigurationModal.inputTooHighErrorMessage;

  const baseSchema = Yup.number()
    .integer(positiveNumberErrorMessage)
    .positive(positiveNumberErrorMessage)
    .typeError(positiveNumberErrorMessage)
    .required(cmsShopContent.inputRequiredError);

  const minRestrictedSchema = baseSchema.when((_, schema) => {
    return min !== null && min !== undefined
      ? schema.min(min, inputTooLowErrorTemplate.replace('{x}', min.toString()))
      : schema;
  });

  const minMaxRestrictedSchema = minRestrictedSchema.when((_, schema) =>
    max !== null && max !== undefined
      ? schema.max(max, inputTooHighErrorTemplate.replace('{x}', max.toString()))
      : schema,
  );

  return minMaxRestrictedSchema;
};
